import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { BadgeCheck, Truck, RotateCcw, LockKeyhole, Wrench, Loader2 } from "lucide-react";
import { api, waLink } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

function Counter({ to }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.4 });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 1.8,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);

    return <span ref={ref}>{val.toLocaleString("it-IT")}</span>;
}

const GUARANTEES = [
    {
        icon: BadgeCheck,
        title: "Rivenditore autorizzato",
        text: "Vendita ufficiale dei migliori marchi in commercio.",
    },
    {
        icon: Truck,
        title: "Consegna rapida",
        text: "Il ricambio o l'elettrodomestico da te ordinato, subito pronto.",
    },
    {
        icon: RotateCcw,
        title: "Soddisfatti o rimborsati",
        text: "Se qualcosa non torna, lo sistemiamo. In trent'anni, una sola regola: il cliente prima di tutto.",
    },
    {
        icon: LockKeyhole,
        title: "Pagamento sicuro in negozio",
        text: "Nessun pagamento online: contanti, bancomat o carta, direttamente da noi.",
    },
];

export default function PercheNoi() {
    const [modello, setModello] = useState("");
    const [problema, setProblema] = useState("");
    const [analisi, setAnalisi] = useState(null);
    const [analisiLoading, setAnalisiLoading] = useState(false);
    const [analisiErrore, setAnalisiErrore] = useState("");

    const analizza = async (e) => {
        e.preventDefault();
        setAnalisiErrore("");
        if (!modello.trim() || !problema.trim()) {
            setAnalisiErrore("Inserisci modello e problema.");
            return;
        }
        setAnalisiLoading(true);
        setAnalisi(null);
        try {
            const res = await api.post("/supporto-tecnico", { modello, problema });
            setAnalisi(res.data);
        } catch (err) {
            setAnalisiErrore(err?.response?.data?.detail || "Analisi non riuscita, riprova tra poco.");
        } finally {
            setAnalisiLoading(false);
        }
    };

    return (
        <section id="perche-noi" className="py-24 md:py-32 border-t border-line">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    eyebrow="Perché sceglierci"
                    title={
                        <>
                            La fiducia è un
                            <br />
                            lavoro di tutti i giorni.
                        </>
                    }
                />

                <div className="mt-14 grid lg:grid-cols-12 gap-6">
                    <Reveal className="lg:col-span-7">
                        <article className="relative overflow-hidden border border-line bg-card h-full min-h-[300px] flex flex-col justify-end">
                            <img
                                src="https://images.unsplash.com/photo-1774273177331-4c19e9912071?auto=format&fit=crop&w=1200&q=80"
                                alt="Magazzino ricambi CG Service"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover opacity-40"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                            <div className="relative p-8 md:p-10">
                                <p className="font-display text-6xl md:text-7xl font-black tracking-tighter text-primary">
                                    <Counter to={12000} />+
                                </p>
                                <p className="mt-3 max-w-md text-muted-foreground leading-relaxed">
                                    ricambi in catalogo per tutti i marchi in
                                    commercio: la parte giusta esiste sempre,
                                    da noi la trovi.
                                </p>
                                <p className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-foreground/60">
                                    Stock pronto · Consegna immediata
                                </p>
                            </div>
                        </article>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-5">
                        <article className="relative overflow-hidden bg-secondary text-secondary-foreground h-full min-h-[300px] p-8 md:p-10 flex flex-col" data-testid="supporto-tecnico">
                            <Wrench className="h-10 w-10 text-primary" strokeWidth={1.6} />
                            <div className="mt-4">
                                <h3 className="font-display text-3xl font-extrabold tracking-tighter leading-tight">
                                    Supporto tecnico
                                </h3>
                                <p className="mt-3 text-foreground/80 leading-relaxed">
                                    Il tuo elettrodomestico ha un problema?
                                    Inserisci il modello e la problematica:
                                    ti mostriamo subito le possibili cause.
                                </p>
                                <form onSubmit={analizza} className="mt-5 space-y-2.5">
                                    <input
                                        data-testid="supporto-modello"
                                        value={modello}
                                        onChange={(e) => setModello(e.target.value)}
                                        placeholder="Solo modello (es. Candy Smart Pro 9 kg)"
                                        className="w-full bg-background/70 border border-foreground/15 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors"
                                    />
                                    <input
                                        data-testid="supporto-problema"
                                        value={problema}
                                        onChange={(e) => setProblema(e.target.value)}
                                        placeholder="Problema (es. non fa lo scarico, non raffredda…)"
                                        className="w-full bg-background/70 border border-foreground/15 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors"
                                    />
                                    <button
                                        type="submit"
                                        data-testid="supporto-analizza"
                                        disabled={analisiLoading}
                                        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 font-display font-bold text-sm tracking-tight hover:bg-primary/90 transition-colors disabled:opacity-60"
                                    >
                                        {analisiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                                        {analisiLoading ? "Sto analizzando…" : "Trova le possibili cause"}
                                    </button>
                                    {analisiErrore && (
                                        <p data-testid="supporto-errore" className="text-xs text-destructive">
                                            {analisiErrore}
                                        </p>
                                    )}
                                </form>

                                {analisi && (
                                    <div className="mt-5 border-t border-foreground/15 pt-5" data-testid="supporto-risultato">
                                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-foreground/70">
                                            Possibili cause
                                        </p>
                                        <ol className="mt-3 space-y-3">
                                            {analisi.possibili_cause.map((c, i) => (
                                                <li key={i} className="flex gap-3">
                                                    <span className="font-mono text-xs text-primary shrink-0 mt-0.5">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span>
                                                        <span className="block text-sm font-bold">{c.titolo}</span>
                                                        <span className="block text-xs leading-relaxed text-foreground/75 mt-0.5">
                                                            {c.spiegazione}
                                                        </span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                        {analisi.consiglio && (
                                            <p className="mt-4 text-xs leading-relaxed text-foreground/80 border-l-2 border-primary pl-3">
                                                {analisi.consiglio}
                                            </p>
                                        )}
                                        {analisi.serve_tecnico && (
                                            <p className="mt-3 text-xs leading-relaxed text-foreground/80">
                                                Serve l'intervento di un tecnico?{" "}
                                                <a
                                                    data-testid="supporto-whatsapp"
                                                    href={waLink(
                                                        `Ciao CG Service! Il mio ${modello} ha questo problema: ${problema}. Mi indicate un tecnico di fiducia?`,
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline underline-offset-2 font-bold hover:text-primary transition-colors"
                                                >
                                                    Scrivici su WhatsApp
                                                </a>{" "}
                                                e ti indirizziamo noi.
                                            </p>
                                        )}
                                        <p className="mt-3 font-mono text-[9px] tracking-[0.15em] uppercase text-foreground/50">
                                            Indicazioni orientanti · per l'intervento affidati a un tecnico
                                        </p>
                                    </div>
                                )}
                            </div>
                        </article>
                    </Reveal>

                    {GUARANTEES.map((g, i) => (
                        <Reveal key={g.title} delay={0.08 * i} className="lg:col-span-4">
                            <article className="group border border-line bg-card p-7 hover:border-primary/60 transition-colors duration-300 h-full">
                                <g.icon
                                    className="h-7 w-7 text-primary transition-transform duration-300 group-hover:-translate-y-1"
                                    strokeWidth={1.6}
                                />
                                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">
                                    {g.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                    {g.text}
                                </p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}